import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
  Node,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { mockAgents } from '../data/mockData';
import PageFooter from '../components/PageFooter';
import workflowAPI from '../services/api';
import { WorkflowSerializer, WorkflowValidator, WorkflowUtils } from '../lib/serialization';
import { ExecutionSerializer } from '../lib/execution';
import './WorkflowDesigner.css';

const nodeTypes = [
  { type: 'agent', label: 'Agent Node', icon: '🤖' },
  { type: 'task', label: 'Task Node', icon: '🔧' },
  { type: 'decision', label: 'Decision Node', icon: '🔀' },
  { type: 'wait', label: 'Wait Node', icon: '⏱️' },
  { type: 'parallel', label: 'Parallel Node', icon: '⚡' },
  { type: 'loop', label: 'Loop Node', icon: '🔄' },
  { type: 'approval', label: 'Manual Approval', icon: '✋' },
];

const initialNodes: Node[] = [
  {
    id: '1',
    type: 'input',
    data: { label: 'Start' },
    position: { x: 250, y: 50 },
  },
];

const WorkflowDesigner: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [workflowName, setWorkflowName] = useState('New Workflow');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [viewingJson, setViewingJson] = useState(false);
  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [showTopHeader, setShowTopHeader] = useState(true);

  // Load workflow on mount if ID or template is provided
  useEffect(() => {
    const id = searchParams.get('id');
    const template = searchParams.get('template');

    // Check if loading from template via sessionStorage
    const loadedWorkflow = sessionStorage.getItem('loadedWorkflow');
    const loadedFromTemplate = sessionStorage.getItem('loadedFromTemplate');

    if (loadedWorkflow && loadedFromTemplate === 'true') {
      // Load workflow from sessionStorage
      try {
        const workflow = JSON.parse(loadedWorkflow);
        setWorkflowName(workflow.name || 'Workflow from Template');
        setWorkflowDescription(workflow.description || '');

        // Templates already have START and END nodes, so just load them directly
        setNodes(workflow.nodes);
        setEdges(workflow.edges || []);

        // Clear sessionStorage
        sessionStorage.removeItem('loadedWorkflow');
        sessionStorage.removeItem('loadedFromTemplate');
        sessionStorage.removeItem('templateId');
      } catch (err) {
        console.error('Error loading workflow from template:', err);
      }
    } else if (id) {
      loadWorkflow(id);
    } else if (template === 'sdlc') {
      loadSDLCTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const addNode = (type: string) => {
    // Position nodes in the visible center of the canvas
    const baseX = 200; // Center X
    const baseY = 100; // Center Y
    const offset = (nodes.length % 10) * 40; // Offset each new node, reset after 10

    const newNode: Node = {
      id: `${Date.now()}`, // Use timestamp for unique ID
      type: 'default',
      data: { label: type },
      position: { x: baseX + offset, y: baseY + offset },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    // Single click selects the node and shows properties
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    // Deselect node when clicking on canvas
    setSelectedNode(null);
  }, []);

  // Load existing workflow
  const loadWorkflow = async (id: string) => {
    try {
      setLoading(true);
      const workflow = await workflowAPI.getWorkflow(id);

      setWorkflowId(workflow.id!);
      setWorkflowName(workflow.name);
      setWorkflowDescription(workflow.description || '');
      setNodes(workflow.nodes);
      setEdges(workflow.edges);

      setSaveStatus('idle');
    } catch (error: any) {
      setErrorMessage(`Failed to load workflow: ${error.message}`);
      setSaveStatus('error');
    } finally {
      setLoading(false);
    }
  };

  // Load SDLC template
  const loadSDLCTemplate = async () => {
    try {
      setLoading(true);
      const template = await workflowAPI.getSDLCTemplate();

      if (template.workflow) {
        setWorkflowName('AI SDLC Workflow (from template)');
        setWorkflowDescription(template.description || '');
        setNodes(template.workflow.nodes);
        setEdges(template.workflow.edges);
      }

      setSaveStatus('idle');
    } catch (error: any) {
      setErrorMessage(`Failed to load template: ${error.message}`);
      setSaveStatus('error');
    } finally {
      setLoading(false);
    }
  };

  // Save workflow (create or update)
  const saveWorkflow = async () => {
    try {
      setSaveStatus('saving');
      setErrorMessage('');

      // Validate workflow name
      if (!workflowName || workflowName.trim() === '' || workflowName === 'New Workflow') {
        setErrorMessage('Please provide a workflow name before saving');
        setSaveStatus('error');
        return;
      }

      // Create workflow definition
      const workflowDef = WorkflowSerializer.fromReactFlow(nodes as any, edges as any, {
        name: workflowName,
        description: workflowDescription
      });

      // Validate workflow
      const validation = WorkflowValidator.validate(workflowDef);
      if (!validation.valid) {
        setErrorMessage(`Validation failed: ${validation.errors.map(e => e.message).join(', ')}`);
        setSaveStatus('error');
        return;
      }

      // Show warnings if any
      if (validation.warnings.length > 0) {
        console.warn('Workflow warnings:', validation.warnings);
      }

      // Create execution-ready workflow
      const executableWorkflow = ExecutionSerializer.toExecutableWorkflow(
        nodes as any,
        edges as any,
        {
          id: workflowId || undefined,
          name: workflowName,
          description: workflowDescription,
          version: 1
        }
      );

      // Save to backend with both visual and execution formats
      let savedWorkflow;
      if (workflowId) {
        // Update existing
        savedWorkflow = await workflowAPI.updateWorkflow(workflowId, {
          name: workflowName,
          description: workflowDescription,
          nodes: nodes as any,
          edges,
          configuration: {
            ...executableWorkflow.configuration,
            executableWorkflow: {
              nodes: executableWorkflow.nodes
              // edges are already at top level, no need to duplicate
            }
          }
        });
      } else {
        // Create new
        savedWorkflow = await workflowAPI.createWorkflow({
          name: workflowName,
          description: workflowDescription,
          nodes: nodes as any,
          edges,
          configuration: {
            ...executableWorkflow.configuration,
            executableWorkflow: {
              nodes: executableWorkflow.nodes
              // edges are already at top level, no need to duplicate
            }
          },
          created_by: 'current-user' // TODO: Get from auth context
        });
        setWorkflowId(savedWorkflow.id!);
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);

    } catch (error: any) {
      // Handle duplicate name error specifically
      if (error.response?.status === 409 || error.response?.data?.code === 'DUPLICATE_NAME') {
        setErrorMessage(error.response?.data?.error || 'A workflow with this name already exists. Please choose a different name.');
      } else {
        setErrorMessage(`Failed to save workflow: ${error.response?.data?.error || error.message}`);
      }
      setSaveStatus('error');
    }
  };

  const executeWorkflow = async () => {
    if (!workflowId) {
      alert('Please save the workflow before executing');
      return;
    }

    try {
      // Execute the workflow
      const result = await workflowAPI.executeWorkflow(workflowId, {});

      // Navigate to Executions tab immediately
      navigate('/executions');

      // Show success message after navigation
      setTimeout(() => {
        alert(`Workflow execution started successfully!\n\nExecution ID: ${result.execution_id}\nTemporal Workflow ID: ${result.temporal_workflow_id}`);
      }, 100);
    } catch (err: any) {
      alert(`Failed to execute workflow: ${err.message}`);
    }
  };

  const handleExport = () => {
    const workflowDef = WorkflowSerializer.fromReactFlow(nodes as any, edges as any, {
      name: workflowName,
      description: workflowDescription
    });
    WorkflowUtils.exportToFile(workflowDef);
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const workflow = await WorkflowUtils.importFromFile(file);
      setWorkflowName(workflow.name);
      setWorkflowDescription(workflow.description || '');
      setNodes(workflow.nodes);
      setEdges(workflow.edges);
      setWorkflowId(null); // Clear ID to create new workflow on save
    } catch (error: any) {
      alert(`Import failed: ${error.message}`);
    }
  };

  const handleViewJson = () => {
    setViewingJson(true);
  };

  const handleCloseJson = () => {
    setViewingJson(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('JSON copied to clipboard!');
  };

  const getCurrentWorkflowJson = () => {
    // Generate execution-ready workflow
    const executableWorkflow = ExecutionSerializer.toExecutableWorkflow(
      nodes as any,
      edges as any,
      {
        id: workflowId || undefined,
        name: workflowName,
        description: workflowDescription,
        version: 1
      }
    );

    return {
      id: workflowId,
      name: workflowName,
      description: workflowDescription,
      nodes,
      edges,
      status: 'draft',
      version: 1,
      configuration: {
        ...executableWorkflow.configuration,
        executableWorkflow: {
          nodes: executableWorkflow.nodes
          // edges are already at top level, no need to duplicate
        }
      }
    };
  };

  if (loading) {
    return (
      <div className="workflow-designer">
        <div className="loading-container">
          <p>Loading workflow...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="workflow-designer">
      {/* Top Header Toggle Button */}
      <button
        className={`header-toggle ${!showTopHeader ? 'collapsed' : ''}`}
        onClick={() => setShowTopHeader(!showTopHeader)}
        title={showTopHeader ? 'Hide header' : 'Show header'}
      >
        {showTopHeader ? '▲' : '▼'}
      </button>

      {showTopHeader && (
        <div className="designer-header">
          <div className="header-left">
            <div className="workflow-info">
              <input
                type="text"
                value={workflowName}
                onChange={(e) => {
                  setWorkflowName(e.target.value);
                  if (errorMessage.includes('workflow name')) {
                    setErrorMessage('');
                    setSaveStatus('idle');
                  }
                }}
                className={`workflow-name-input ${errorMessage.includes('workflow name') ? 'error' : ''}`}
                placeholder="Enter workflow name (required) *"
              />
              <input
                type="text"
                value={workflowDescription}
                onChange={(e) => setWorkflowDescription(e.target.value)}
                className="workflow-description-input"
                placeholder="Description (optional)"
              />
            </div>
          </div>
          <div className="header-right">
            <div className="save-status">
              {saveStatus === 'saving' && <span className="status-saving">Saving...</span>}
              {saveStatus === 'saved' && <span className="status-saved">✓ Saved</span>}
              {saveStatus === 'error' && <span className="status-error">⚠ Error</span>}
            </div>
            <label className="btn-secondary" style={{ cursor: 'pointer' }}>
              Import
              <input
                type="file"
                accept=".json"
                onChange={handleImport}
                style={{ display: 'none' }}
              />
            </label>
            <button className="btn-secondary" onClick={handleExport}>Export</button>
            <button className="btn-secondary" onClick={handleViewJson}>View JSON</button>
            <button className="btn-secondary" onClick={saveWorkflow} disabled={saveStatus === 'saving'}>
              {workflowId ? 'Update' : 'Save'}
            </button>
            <button className="btn-primary" onClick={executeWorkflow}>Execute</button>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="error-banner">
          <span>⚠️ {errorMessage}</span>
          <button onClick={() => setErrorMessage('')}>×</button>
        </div>
      )}

      <div className="designer-content">
        {showLeftPanel && (
          <div className="node-palette">
            <h3>Node Palette</h3>
            <div className="palette-search">
              <input type="text" placeholder="Search nodes..." />
            </div>

            <div className="palette-section">
              <h4>Node Types</h4>
              {nodeTypes.map((nodeType) => (
                <div
                  key={nodeType.type}
                  className="palette-item"
                  onClick={() => addNode(nodeType.label)}
                >
                  <span className="palette-icon">{nodeType.icon}</span>
                  <span>{nodeType.label}</span>
                </div>
              ))}
            </div>

            <div className="palette-section">
              <h4>Available Agents</h4>
              {mockAgents.slice(0, 6).map((agent) => (
                <div
                  key={agent.id}
                  className="palette-item"
                  onClick={() => addNode(agent.name)}
                >
                  <span className="palette-icon">🤖</span>
                  <span className="palette-label">{agent.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="canvas-container">
          {/* Left panel toggle button */}
          <button
            className={`panel-toggle-btn left-toggle ${!showLeftPanel ? 'collapsed' : ''}`}
            onClick={() => setShowLeftPanel(!showLeftPanel)}
            title={showLeftPanel ? 'Hide left panel' : 'Show left panel'}
          >
            {showLeftPanel ? '◀' : '▶'}
          </button>

          {/* Right panel toggle button */}
          <button
            className={`panel-toggle-btn right-toggle ${!showRightPanel ? 'collapsed' : ''}`}
            onClick={() => setShowRightPanel(!showRightPanel)}
            title={showRightPanel ? 'Hide right panel' : 'Show right panel'}
          >
            {showRightPanel ? '▶' : '◀'}
          </button>

          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodesDraggable={true}
            nodesConnectable={true}
            selectNodesOnDrag={false}
            fitView
          >
            <Background />
            <Controls />
            <MiniMap />
          </ReactFlow>
        </div>

        {showRightPanel && (
          <div className="properties-panel">
            <h3>Properties</h3>
            {selectedNode ? (
              <div className="properties-content">
                <div className="property-group">
                  <label>Node ID</label>
                  <input type="text" value={selectedNode.id} readOnly />
                </div>
                <div className="property-group">
                  <label>Label</label>
                  <input
                    type="text"
                    value={selectedNode.data.label}
                    onChange={(e) => {
                      const updatedLabel = e.target.value;
                      setNodes((nds) =>
                        nds.map((node) =>
                          node.id === selectedNode.id
                            ? { ...node, data: { ...node.data, label: updatedLabel } }
                            : node
                        )
                      );
                      // Update selected node to reflect changes
                      setSelectedNode((prev) =>
                        prev ? { ...prev, data: { ...prev.data, label: updatedLabel } } : null
                      );
                    }}
                  />
                </div>
                <div className="property-group">
                  <label>Timeout (seconds)</label>
                  <input type="number" defaultValue="300" />
                </div>
                <div className="property-group">
                  <label>Retry Count</label>
                  <input type="number" defaultValue="3" />
                </div>
              </div>
            ) : (
              <p className="no-selection">Select a node to view properties</p>
            )}
          </div>
        )}
      </div>

      {viewingJson && (
        <div className="json-modal-overlay" onClick={handleCloseJson}>
          <div className="json-modal" onClick={(e) => e.stopPropagation()}>
            <div className="json-modal-header">
              <h2>Workflow JSON</h2>
              <button className="close-btn" onClick={handleCloseJson}>×</button>
            </div>
            <div className="json-modal-actions">
              <button
                className="btn-secondary"
                onClick={() => copyToClipboard(JSON.stringify(getCurrentWorkflowJson(), null, 2))}
              >
                Copy to Clipboard
              </button>
            </div>
            <pre className="json-content">
              {JSON.stringify(getCurrentWorkflowJson(), null, 2)}
            </pre>
          </div>
        </div>
      )}

      <PageFooter />
    </div>
  );
};

export default WorkflowDesigner;

